import { useCallback, useEffect, useState } from 'react'
import { KeyRound, Lock, Plus, Users, X, ShieldCheck, Copy, Trash2, SlidersHorizontal, Sparkles } from 'lucide-react'
import {
  Modal, Select, ToastProvider, useToast, apiGet, adminMutate, usePermissions, RowMenu,
} from './primitives.js'
import { Field, TextInput } from './form.js'
import { Combobox, SegmentedControl, type ComboItem } from './pickers.js'
import { SettingsCrumb } from './SettingsCrumb.js'
import { ConfirmDialog } from '../auth/Dialog.js'
import { Link, navigate } from '../router.js'
import { permMeta } from './catalog.js'
import { useSaveBar, guardedClose } from '../shell/editing.js'
import './droits.css'

/**
 * /settings/roles — LES MODÈLES DE DROITS (08/10/2026).
 *
 * « Cases par permission, mais on pourra créer des modèles de permissions »
 * (l'exploitant). Un modèle EST un rôle, présenté comme réutilisable : une
 * personne en reçoit un ou plusieurs (union de leurs droits), et ses
 * exceptions individuelles l'emportent. Aucun second système.
 *
 * Ce que cet écran fait — et ne fait plus :
 *  · il LISTE les modèles (intégrés, proposés, personnalisés) avec, pour
 *    chacun, ce qu'il couvre par famille et combien de comptes l'utilisent ;
 *  · il CRÉE (vide, copie d'un modèle, droits d'un compte), DUPLIQUE,
 *    RENOMME, DÉCRIT et SUPPRIME un modèle inutilisé — le serveur garde ;
 *  · il n'ÉDITE PLUS les droits à la case : l'ancien éditeur (PATCH qui
 *    remplaçait `role_permission`) ignorait les overrides de rôle et
 *    n'écrivait aucun historique — un droit accordé ailleurs restait accordé
 *    alors que la case s'affichait décochée. Les droits d'un modèle se
 *    modifient dans l'éditeur par familles (`/settings/roles/:key/permissions`),
 *    seul chemin d'écriture, avec aperçu d'impact, motif et historique.
 *  · le Super Admin reste protégé (il possède tout le catalogue), les modèles
 *    intégrés ne se suppriment pas.
 */

interface FamilyCount { id: string; label: string; count: number }
interface TemplateRow {
  key: string; name: string; description: string | null; is_builtin: boolean
  users: number; permissions: number
  suggested: boolean; protected: boolean; families: FamilyCount[]; delete_blocker: string | null
}
interface RoleDetail {
  key: string; name: string; description: string | null; is_builtin: boolean; protected: boolean
  suggested: boolean; families: FamilyCount[]; delete_blocker: string | null
  permissions: string[]; members: { id: string; display_name: string; email: string }[]
}

/* ─── garde de forme : une réponse inattendue n'invente rien ─── */
const isRec = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null
const str = (v: unknown): string | null => (typeof v === 'string' ? v : null)
function shapeFamilies(v: unknown): FamilyCount[] {
  return Array.isArray(v) ? v.flatMap((f) => isRec(f) && typeof f['id'] === 'string' && typeof f['count'] === 'number'
    ? [{ id: f['id'], label: str(f['label']) ?? f['id'], count: f['count'] }] : []) : []
}
function shapeTemplate(v: unknown): TemplateRow | null {
  if (!isRec(v) || typeof v['key'] !== 'string' || typeof v['name'] !== 'string') return null
  return {
    key: v['key'], name: v['name'], description: str(v['description']), is_builtin: v['is_builtin'] === true,
    users: typeof v['users'] === 'number' ? v['users'] : 0, permissions: typeof v['permissions'] === 'number' ? v['permissions'] : 0,
    suggested: v['suggested'] === true, protected: v['protected'] === true || v['key'] === 'super_admin',
    families: shapeFamilies(v['families']), delete_blocker: str(v['delete_blocker']),
  }
}
function shapeDetail(v: unknown): RoleDetail | null {
  const t = shapeTemplate(v)
  if (!t || !isRec(v)) return null
  return {
    ...t,
    permissions: Array.isArray(v['permissions']) ? v['permissions'].filter((p): p is string => typeof p === 'string') : [],
    members: Array.isArray(v['members']) ? v['members'].flatMap((m) => isRec(m) && typeof m['id'] === 'string'
      ? [{ id: m['id'], display_name: str(m['display_name']) ?? '—', email: str(m['email']) ?? '' }] : []) : [],
  }
}

const plural = (n: number, one: string, many: string): string => `${n} ${n > 1 ? many : one}`

function typePill(t: { is_builtin: boolean; suggested: boolean; protected: boolean }) {
  if (t.protected) return <span className="lc-pill lc-pill--idle"><Lock size={12} />Protégé</span>
  if (t.is_builtin) return <span className="lc-pill lc-pill--idle"><Lock size={12} />Intégré</span>
  if (t.suggested) return <span className="lc-pill"><Sparkles size={12} />Proposé</span>
  return <span className="lc-pill">Personnalisé</span>
}

function TemplatesInner() {
  const toast = useToast()
  const { permissions: myPerms, authenticated } = usePermissions()
  const canWrite = authenticated && myPerms.includes('role.write')

  const [rows, setRows] = useState<TemplateRow[] | null>(null)
  const [catalogSize, setCatalogSize] = useState<number | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [detail, setDetail] = useState<RoleDetail | null>(null)
  // `open` pilote l'animation de sortie ; `detail` n'est purgé qu'à onExited.
  const [detailOpen, setDetailOpen] = useState(false)
  const [creation, setCreation] = useState(false)
  const [creationMounted, setCreationMounted] = useState(false)
  const [toDuplicate, setToDuplicate] = useState<TemplateRow | null>(null)
  const [toDelete, setToDelete] = useState<TemplateRow | null>(null)

  const charger = useCallback(() => {
    apiGet<unknown>('/api/v1/admin/roles').then((j) => {
      if (!isRec(j) || !Array.isArray(j['roles'])) { setLoadError(isRec(j) ? str(j['message']) ?? 'Réponse inattendue du serveur.' : 'Réponse inattendue du serveur.'); setRows([]); return }
      setLoadError(null)
      setRows(j['roles'].map(shapeTemplate).filter((t): t is TemplateRow => t !== null))
      // Le catalogue porte aussi les interrupteurs (gate.*) : seuls les droits se comptent.
      setCatalogSize(Array.isArray(j['catalog']) ? j['catalog'].filter((p) => isRec(p) && typeof p['key'] === 'string' && !p['key'].startsWith('gate.')).length : null)
    }).catch(() => { setLoadError('Le serveur n’a pas répondu.'); setRows([]) })
  }, [])
  useEffect(() => { charger() }, [charger])

  const ouvrir = (key: string) => {
    apiGet<unknown>(`/api/v1/admin/roles/${key}`).then((d) => {
      const shaped = shapeDetail(d)
      if (shaped) { setDetail(shaped); setDetailOpen(true) } else toast('error', 'Ce modèle est introuvable : il a peut-être été supprimé.')
    }).catch(() => toast('error', 'Le serveur n’a pas répondu.'))
  }

  const supprimer = async (t: TemplateRow) => {
    const r = await adminMutate<{ error?: string; message?: string }>('DELETE', `/api/v1/admin/roles/${t.key}`)
    if (r.status === 200) { toast('ok', `Modèle « ${t.name} » supprimé — aucun compte ne l’utilisait.`); setDetailOpen(false); charger() }
    else toast('error', r.data.message ?? 'Le modèle n’a pas été supprimé.')
  }

  const groupes: { key: string; titre: string; hint: string; list: TemplateRow[] }[] = [
    { key: 'perso', titre: 'Personnalisés', hint: 'Créés ici : modifiables, supprimables tant qu’aucun compte ne les utilise.', list: (rows ?? []).filter((r) => !r.is_builtin && !r.suggested) },
    // Les proposés forment une échelle (chacun contient le précédent) : du plus court au plus large.
    { key: 'proposes', titre: 'Proposés', hint: 'Prêts à l’emploi, attribués à personne : appliquez-les tels quels, modifiez-les ou supprimez-les.', list: (rows ?? []).filter((r) => !r.is_builtin && r.suggested).sort((a, b) => a.permissions - b.permissions) },
    { key: 'integres', titre: 'Intégrés', hint: 'Livrés avec Gestion : ils ne se suppriment pas, et le Super Admin ne se modifie pas.', list: (rows ?? []).filter((r) => r.is_builtin) },
  ]

  return (
    <main className="lc-page lc-stagger">
      <SettingsCrumb />
      <h1 className="lc-title">Modèles de droits</h1>
      <p className="lc-subtitle">
        Un modèle regroupe des droits à réutiliser. Une personne peut en cumuler plusieurs :
        elle reçoit tout ce qu’ils accordent, et ses exceptions individuelles l’emportent.
      </p>

      <div className="ad-toolbar">
        {canWrite ? (
          <button className="lc-btn lc-btn--primary" onClick={() => { setCreation(true); setCreationMounted(true) }}>
            <Plus size={15} /> Nouveau modèle
          </button>
        ) : null}
        <span className="ad-count">
          {rows === null ? 'Chargement en cours' : `${plural(rows.length, 'modèle', 'modèles')}${catalogSize !== null ? ` · ${plural(catalogSize, 'droit', 'droits')} au catalogue` : ''}`}
        </span>
      </div>

      {loadError ? <p className="adm-note" role="alert"><strong>Modèles indisponibles.</strong> {loadError}</p> : null}

      {groupes.map((g) => g.list.length === 0 && g.key !== 'perso' ? null : (
        <section key={g.key} className="dr-section" aria-labelledby={`dr-${g.key}`}>
          <div className="dr-section__head">
            <h2 className="dr-section__title" id={`dr-${g.key}`}>{g.titre}</h2>
            <span className="dr-section__hint">{g.hint}</span>
          </div>
          {g.list.length === 0 ? (
            <p className="dr-hint">{rows === null ? 'Chargement en cours' : 'Aucun modèle personnalisé pour l’instant. « Nouveau modèle » en crée un, vide ou à partir des droits d’un compte.'}</p>
          ) : (
            <div className="dr-grid">
              {g.list.map((t) => (
                <TemplateCard key={t.key} t={t} canWrite={canWrite}
                  onOpen={() => ouvrir(t.key)}
                  onDuplicate={() => setToDuplicate(t)}
                  onDelete={() => setToDelete(t)} />
              ))}
            </div>
          )}
        </section>
      ))}

      {detail ? (
        <TemplateModal
          detail={detail}
          open={detailOpen}
          canWrite={canWrite}
          onClose={() => setDetailOpen(false)}
          onExited={() => setDetail(null)}
          onSaved={() => { ouvrir(detail.key); charger() }}
          onDuplicate={() => { const t = rows?.find((r) => r.key === detail.key); if (t) setToDuplicate(t) }}
          onDelete={() => { const t = rows?.find((r) => r.key === detail.key); if (t) setToDelete(t) }}
        />
      ) : null}

      {creationMounted ? (
        <CreateTemplateModal
          open={creation}
          templates={rows ?? []}
          onClose={() => setCreation(false)}
          onExited={() => setCreationMounted(false)}
          onCreated={(key, name) => {
            setCreation(false)
            charger()
            toast('ok', `Modèle « ${name} » créé.`, { action: { label: 'Régler ses droits', onAction: () => navigate(`/settings/roles/${key}/permissions`) } })
          }}
        />
      ) : null}

      <DuplicateModal
        source={toDuplicate}
        onClose={() => setToDuplicate(null)}
        onDone={(key, name) => {
          setToDuplicate(null)
          charger()
          toast('ok', `Copie « ${name} » créée.`, { action: { label: 'Régler ses droits', onAction: () => navigate(`/settings/roles/${key}/permissions`) } })
        }}
      />

      <ConfirmDialog
        open={toDelete !== null}
        danger
        title={`Supprimer le modèle « ${toDelete?.name ?? ''} » ?`}
        confirmLabel="Supprimer le modèle"
        onConfirm={() => { const t = toDelete; setToDelete(null); if (t) void supprimer(t) }}
        onClose={() => setToDelete(null)}
      >
        Aucun compte ne l’utilise : personne ne perd de droit. Le modèle et ses réglages
        disparaissent ; son historique reste consultable dans l’audit.
      </ConfirmDialog>
    </main>
  )
}

/* ═══════════ carte d'un modèle ═══════════ */

function TemplateCard(props: { t: TemplateRow; canWrite: boolean; onOpen: () => void; onDuplicate: () => void; onDelete: () => void }) {
  const t = props.t
  return (
    <article className="dr-card" aria-label={`Modèle ${t.name}`}>
      <header className="dr-card__head">
        <span className="lc-avatar dr-card__icon" style={{ background: t.protected ? 'var(--grad-rose)' : t.suggested ? 'var(--grad-emerald)' : 'var(--grad-blue)' }}>
          <KeyRound size={15} />
        </span>
        <div className="dr-card__title">
          <span className="dr-card__name">{t.name}</span>
          <span className="dr-card__pills">{typePill(t)}</span>
        </div>
      </header>
      <p className="dr-card__desc">{t.description ?? 'Aucune description.'}</p>
      <div className="dr-chips" aria-label="Droits par famille">
        {t.protected
          ? <span className="dr-chip">Tout le catalogue</span>
          : t.families.length > 0
            ? t.families.map((f) => <span key={f.id} className="dr-chip">{f.label} · {f.count}</span>)
            : <span className="dr-chip">Aucun droit pour l’instant</span>}
      </div>
      <div className="dr-card__facts">
        <span className="dr-inline"><Users size={13} aria-hidden="true" /> {t.users === 0 ? 'Appliqué à aucun compte' : `Appliqué à ${plural(t.users, 'compte', 'comptes')}`}</span>
        <span className="dr-inline"><ShieldCheck size={13} aria-hidden="true" /> {t.protected ? 'Tout le catalogue' : plural(t.permissions, 'droit', 'droits')}</span>
      </div>
      <div className="dr-card__actions">
        <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={props.onOpen}>Détail</button>
        {!t.protected ? (
          <Link href={`/settings/roles/${t.key}/permissions`} className="lc-btn lc-btn--sm">
            <SlidersHorizontal size={13} /> {props.canWrite ? 'Modifier les droits' : 'Voir les droits'}
          </Link>
        ) : null}
        {props.canWrite ? (
          <RowMenu label={`Autres actions sur ${t.name}`} items={[
            { label: 'Dupliquer', icon: <Copy size={14} />, onClick: props.onDuplicate },
            { label: 'Supprimer', icon: <Trash2 size={14} />, danger: true, onClick: props.onDelete, disabled: t.delete_blocker !== null, reason: t.delete_blocker ?? undefined },
          ]} />
        ) : null}
      </div>
    </article>
  )
}

/* ═══════════ MODALE DÉTAIL : nom et description (Save Bar), droits en lecture ═══════════ */

function TemplateModal(props: {
  detail: RoleDetail
  open: boolean
  canWrite: boolean
  onClose: () => void
  onExited: () => void
  onSaved: () => void
  onDuplicate: () => void
  onDelete: () => void
}) {
  const d = props.detail
  const editable = props.canWrite && !d.protected

  const [name, setName] = useState(d.name)
  const [description, setDescription] = useState(d.description ?? '')
  useEffect(() => { setName(d.name); setDescription(d.description ?? '') }, [d])

  const dirty = editable && (name !== d.name || description !== (d.description ?? ''))
  useSaveBar({
    dirty,
    count: (name !== d.name ? 1 : 0) + (description !== (d.description ?? '') ? 1 : 0),
    invalid: name.trim().length < 2 ? 'Le nom du modèle fait au moins 2 caractères.' : null,
    successMessage: 'Modèle enregistré.',
    onSave: async () => {
      const r = await adminMutate<{ error?: string; message?: string }>('PATCH', `/api/v1/admin/roles/${d.key}`, { name: name.trim(), description })
      if (r.status >= 300) return r.data.message ?? 'Le modèle n’a pas été enregistré. Rien n’a changé.'
      props.onSaved()
      return true
    },
    onCancel: () => { setName(d.name); setDescription(d.description ?? '') },
  })
  const close = () => guardedClose(dirty, props.onClose)

  // Droits regroupés par famille, avec les libellés humains du catalogue.
  const groupes = d.permissions.reduce<Record<string, string[]>>((acc, k) => {
    const g = permMeta(k).group ?? 'Autres'
    ;(acc[g] ??= []).push(k)
    return acc
  }, {})

  return (
    <Modal open={props.open} onClose={close} onExited={props.onExited} ariaLabel={`Modèle ${d.name}`} width={880} className="lc-modal--editable">
      <header className="lc-modal__head">
        <span className="lc-modal__icon" style={{ background: d.protected ? 'var(--grad-rose)' : 'var(--grad-blue)' }}>
          <KeyRound size={22} />
        </span>
        <div>
          <div className="lc-modal__title">{d.name}</div>
          <div className="lc-modal__pills">
            {typePill(d)}
            <span className="lc-pill lc-pill--idle"><ShieldCheck size={12} />{plural(d.permissions.length, 'droit', 'droits')}</span>
            <span className="lc-pill lc-pill--idle"><Users size={12} />{plural(d.members.length, 'compte', 'comptes')}</span>
          </div>
        </div>
        <button className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close" onClick={close} aria-label="Fermer">
          <X size={18} />
        </button>
      </header>
      <div className="lc-modal__body">
        {d.protected ? (
          <div className="adm-note" style={{ marginTop: 0 }}>
            <strong>Modèle protégé.</strong> Le Super Admin possède tout le catalogue :
            il ne se modifie pas depuis l’interface, et seul un Super Admin peut l’attribuer.
          </div>
        ) : null}

        <div className="ap-group" style={{ marginTop: d.protected ? 'var(--sp-4)' : 0 }}>Informations</div>
        {editable ? (
          <div className="rm-fields">
            <Field label="Nom"><TextInput value={name} maxLength={80} onChange={setName} ariaLabel="Nom du modèle" /></Field>
            <div className="dr-span">
              <Field label="Description" helper="À quoi sert ce modèle, pour qui."><TextInput value={description} maxLength={300} onChange={setDescription} ariaLabel="Description du modèle" /></Field>
            </div>
          </div>
        ) : (
          <p className="lc-card__desc">{d.description ?? 'Aucune description.'}</p>
        )}

        <div className="ap-group">Comptes qui l’appliquent ({d.members.length})</div>
        {d.members.length ? (
          <div className="rm-members">
            {d.members.map((m) => (
              <Link key={m.id} href={`/settings/users/${m.id}`} className="lc-pill lc-pill--idle">{m.display_name}</Link>
            ))}
          </div>
        ) : <p className="lc-card__desc">Appliqué à aucun compte.</p>}

        <div className="ap-group">Droits accordés</div>
        {d.protected ? (
          <p className="lc-card__desc">Tout le catalogue, sans exception.</p>
        ) : d.permissions.length === 0 ? (
          <p className="lc-card__desc">Aucun droit pour l’instant : « Modifier les droits » ouvre l’éditeur par familles.</p>
        ) : (
          <div className="dr-rights">
            {Object.entries(groupes).map(([g, keys]) => (
              <div key={g} className="dr-rights__group">
                <span className="dr-rights__title">{g} · {keys.length}</span>
                <div className="dr-rights__list">
                  {keys.map((k) => <span key={k} className="dr-chip" title={permMeta(k).description}>{permMeta(k).label}</span>)}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="ap-group">Actions</div>
        <div className="dr-block__actions" style={{ marginTop: 0 }}>
          {!d.protected ? (
            <Link href={`/settings/roles/${d.key}/permissions`} className="lc-btn">
              <SlidersHorizontal size={14} /> {props.canWrite ? 'Modifier les droits' : 'Voir les droits par famille'}
            </Link>
          ) : null}
          {props.canWrite ? <button type="button" className="lc-btn lc-btn--ghost" onClick={props.onDuplicate}><Copy size={14} /> Dupliquer</button> : null}
          {props.canWrite && d.delete_blocker === null ? (
            <button type="button" className="lc-btn lc-btn--ghost" onClick={props.onDelete}><Trash2 size={14} /> Supprimer</button>
          ) : null}
        </div>
        {props.canWrite && d.delete_blocker !== null ? <p className="dr-hint">Suppression impossible : {d.delete_blocker}</p> : null}
        <ul className="rm-constraints">
          <li>Les droits d’un modèle se modifient dans l’éditeur par familles : aperçu de l’impact sur chaque compte, motif pour les droits critiques, historique.</li>
          <li>Vous ne pouvez mettre dans un modèle que des droits que vous détenez vous-même (sauf Super Admin).</li>
        </ul>
      </div>
    </Modal>
  )
}

/* ═══════════ MODALE CRÉATION — point de départ, puis l'éditeur par familles ═══════════ */

type Depart = 'EMPTY' | 'ROLE' | 'USER'

function CreateTemplateModal(props: {
  open: boolean
  templates: TemplateRow[]
  onClose: () => void
  onExited: () => void
  onCreated: (key: string, name: string) => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [depart, setDepart] = useState<Depart>('EMPTY')
  const [fromRole, setFromRole] = useState('')
  const [fromUser, setFromUser] = useState<string | null>(null)
  const [users, setUsers] = useState<ComboItem[] | null>(null)

  useEffect(() => {
    if (depart !== 'USER' || users !== null) return
    apiGet<unknown>('/api/v1/admin/users?limit=100').then((j) => {
      setUsers(isRec(j) && Array.isArray(j['items'])
        ? j['items'].flatMap((u) => isRec(u) && typeof u['id'] === 'string' && typeof u['display_name'] === 'string'
          ? [{ value: u['id'], label: u['display_name'], ...(typeof u['email'] === 'string' ? { hint: u['email'] } : {}) }] : [])
        : [])
    }).catch(() => setUsers([]))
  }, [depart, users])

  const dirty = name.length > 0 || description.length > 0 || depart !== 'EMPTY'
  const sourceMissing = depart === 'ROLE' && !fromRole ? 'Choisissez le modèle à copier.'
    : depart === 'USER' && !fromUser ? 'Choisissez le compte dont reprendre les droits.' : null

  useSaveBar({
    dirty,
    saveLabel: 'Créer le modèle',
    invalid: name.trim().length < 2 ? 'Donnez un nom au modèle (2 caractères minimum).' : sourceMissing,
    successMessage: 'Modèle créé.',
    onSave: async () => {
      const body: Record<string, unknown> = { name: name.trim() }
      if (description.trim()) body['description'] = description.trim()
      if (depart === 'ROLE') body['from_role'] = fromRole
      if (depart === 'USER' && fromUser) body['from_user'] = fromUser
      const r = await adminMutate<{ key?: string; name?: string; error?: string; message?: string }>('POST', '/api/v1/admin/roles', body)
      if (r.status !== 201 || !r.data.key) return r.data.message ?? 'Le modèle n’a pas été créé. Rien n’a changé.'
      props.onCreated(r.data.key, r.data.name ?? name.trim())
      return true
    },
    onCancel: () => { setName(''); setDescription(''); setDepart('EMPTY'); setFromRole(''); setFromUser(null) },
  })
  const close = () => guardedClose(dirty, props.onClose)

  return (
    <Modal open={props.open} onClose={close} onExited={props.onExited} ariaLabel="Nouveau modèle" width={720} className="lc-modal--editable">
      <header className="lc-modal__head">
        <span className="lc-modal__icon"><Plus size={22} /></span>
        <div>
          <div className="lc-modal__title">Nouveau modèle</div>
          <div className="pm-modal__sub">Nommez-le, choisissez d’où partent ses droits, puis affinez-les dans l’éditeur par familles.</div>
        </div>
        <button className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close" onClick={close} aria-label="Fermer">
          <X size={18} />
        </button>
      </header>
      <div className="lc-modal__body">
        <div className="rm-fields">
          <Field label="Nom"><TextInput value={name} onChange={setName} maxLength={80} placeholder="Ex. Responsable de salle" ariaLabel="Nom du modèle" /></Field>
          <div className="dr-span">
            <Field label="Description" helper="À quoi sert ce modèle, pour qui."><TextInput value={description} onChange={setDescription} maxLength={300} ariaLabel="Description du modèle" /></Field>
          </div>
        </div>
        <div className="ap-group">Point de départ</div>
        <div className="dr-source">
          <SegmentedControl<Depart> value={depart} onChange={setDepart} ariaLabel="Point de départ du modèle" options={[
            { value: 'EMPTY', label: 'Vide' },
            { value: 'ROLE', label: 'Copie d’un modèle' },
            { value: 'USER', label: 'Droits d’un compte' },
          ]} />
          {depart === 'EMPTY' ? <p className="dr-source__help">Le modèle naît sans aucun droit : vous les cochez ensuite, famille par famille.</p> : null}
          {depart === 'ROLE' ? (
            <>
              <p className="dr-source__help">Reprend les droits, refus et périmètres du modèle choisi. La copie est un modèle personnalisé, indépendant de l’original.</p>
              <Select value={fromRole} onChange={setFromRole} placeholder="Choisir un modèle" ariaLabel="Modèle à copier"
                options={props.templates.map((t) => ({ value: t.key, label: `${t.name} · ${plural(t.permissions, 'droit', 'droits')}` }))} />
            </>
          ) : null}
          {depart === 'USER' ? (
            <>
              <p className="dr-source__help">Reprend ce que ce compte peut faire aujourd’hui : ses modèles et ses exceptions combinés, refus déduits. Vous ne pouvez reprendre que des droits que vous détenez vous-même.</p>
              {users === null ? <span className="dr-hint">Chargement des comptes en cours</span>
                : users.length === 0 ? <span className="dr-hint">Aucun compte disponible.</span>
                  : <Combobox value={fromUser} items={users} onChange={setFromUser} placeholder="Choisir un compte" ariaLabel="Compte dont reprendre les droits" leading={<Users size={14} />} />}
            </>
          ) : null}
        </div>
      </div>
    </Modal>
  )
}

/* ═══════════ DUPLIQUER — action atomique, confirmée dans sa propre surface ═══════════ */

function DuplicateModal(props: { source: TemplateRow | null; onClose: () => void; onDone: (key: string, name: string) => void }) {
  const [shown, setShown] = useState<TemplateRow | null>(props.source)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!props.source) return
    setShown(props.source)
    setName(`${props.source.name} (copie)`.slice(0, 80))
    setDescription(props.source.description ?? '')
    setError(null)
  }, [props.source])

  const dupliquer = async () => {
    if (!shown) return
    setBusy(true)
    const body: Record<string, unknown> = { name: name.trim() }
    if (description.trim()) body['description'] = description.trim()
    const r = await adminMutate<{ key?: string; name?: string; error?: string; message?: string }>('POST', `/api/v1/admin/roles/${shown.key}/duplicate`, body)
    setBusy(false)
    if (r.status === 201 && r.data.key) props.onDone(r.data.key, r.data.name ?? name.trim())
    else setError(r.data.message ?? 'La copie n’a pas été créée. Rien n’a changé.')
  }

  return (
    <Modal open={props.source !== null} onClose={props.onClose} ariaLabel="Dupliquer un modèle" width={560} owning>
      <header className="lc-modal__head">
        <span className="lc-modal__icon" style={{ background: 'var(--grad-blue)' }}><Copy size={20} /></span>
        <div>
          <div className="lc-modal__title">Dupliquer « {shown?.name ?? ''} »</div>
          <div className="pm-modal__sub">La copie reprend tous ses droits ; vous la modifiez ensuite sans toucher à l’original.</div>
        </div>
        <button className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close" onClick={props.onClose} aria-label="Fermer"><X size={18} /></button>
      </header>
      <div className="lc-modal__body">
        <Field label="Nom de la copie" error={name.trim().length < 2 ? 'Le nom fait au moins 2 caractères.' : null}>
          <TextInput value={name} onChange={setName} maxLength={80} ariaLabel="Nom de la copie" />
        </Field>
        <Field label="Description"><TextInput value={description} onChange={setDescription} maxLength={300} ariaLabel="Description de la copie" /></Field>
        {error ? <p className="dr-refusal" role="alert">{error}</p> : null}
      </div>
      <footer className="pm-modal__foot">
        <button type="button" className="lc-btn lc-btn--ghost" onClick={props.onClose} disabled={busy}>Annuler</button>
        <button type="button" className="lc-btn lc-btn--primary" onClick={() => void dupliquer()} disabled={busy || name.trim().length < 2}>
          {busy ? 'Copie en cours' : 'Créer la copie'}
        </button>
      </footer>
    </Modal>
  )
}

export function RolesPage() {
  return <ToastProvider><TemplatesInner /></ToastProvider>
}
